import {Subscription} from "rxjs";
import {FormGroup, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {CdkDrag, CdkDragDrop, CdkDragPlaceholder, CdkDropList, moveItemInArray} from "@angular/cdk/drag-drop";

import {NgOptionTemplateDirective, NgSelectComponent} from "@ng-select/ng-select";

import {
  AdCategoryApiService,
  AdCategoryStateService,
  AdSubcategoryApiService,
  AdSubcategoryStateService,
  AdSpecificationOptionApiService,
  AdSpecificationOptionStateService,
  AdSubcategorySpecificationApiService,
  AdSubcategorySpecificationStateService
} from "@numizmat/services/nomenclature";
import {
  AdCategoryResponse,
  AdSubcategoryResponse,
  AdSpecificationOptionResponse,
  AdSubcategorySpecificationResponse,
  AdSubcategoryAssociatedSpecificationResponse
} from "@numizmat/services-models/nomenclature";
import {environment} from "@numizmat/environments";
import {ImageComponent} from "@numizmat/components";
import {PrivateAdDetailResponse} from "@numizmat/services-models/core";
import {AlertService, ImagProcessingService} from "@numizmat/services/common";

export interface PreviewImage {
  imageId?: string;
  imageUrl: string;
  file?: File;
}

@Component({
  selector: 'nz-ad-form-step-01',
  imports: [
    CdkDrag,
    CdkDropList,
    FormsModule,
    ImageComponent,
    NgOptionTemplateDirective,
    NgSelectComponent,
    ReactiveFormsModule,
    CdkDragPlaceholder
  ],
  templateUrl: './ad-form-step-01.component.html',
  styleUrl: './ad-form-step-01.component.scss'
})
export class AdFormStep01Component implements OnInit, OnDestroy {
  @Input() adId?: string;
  @Input() form!: FormGroup;
  @Input() adDetails?: PrivateAdDetailResponse;
  @Input() mode!: 'create' | 'update' | 'create-similar';
  @Output() previewImagesChange: EventEmitter<PreviewImage[]> = new EventEmitter<PreviewImage[]>();
  @Output() changeAdSpecifications: EventEmitter<{ [key: number]: number }> =
    new EventEmitter<{ [key: number]: number }>();

  private alertService: AlertService = inject(AlertService);
  private imagProcessingService: ImagProcessingService = inject(ImagProcessingService);
  private adCategoryApiService: AdCategoryApiService = inject(AdCategoryApiService);
  private adCategoryStateService: AdCategoryStateService = inject(AdCategoryStateService);
  private adSubcategoryApiService: AdSubcategoryApiService = inject(AdSubcategoryApiService);
  private adSubcategoryStateService: AdSubcategoryStateService = inject(AdSubcategoryStateService);
  private adSpecificationOptionApiService: AdSpecificationOptionApiService = inject(AdSpecificationOptionApiService);
  private adSpecificationOptionStateService: AdSpecificationOptionStateService = inject(AdSpecificationOptionStateService);
  private adSubcategorySpecificationApiService: AdSubcategorySpecificationApiService = inject(AdSubcategorySpecificationApiService);
  private adSubcategorySpecificationStateService: AdSubcategorySpecificationStateService = inject(AdSubcategorySpecificationStateService);

  private subs: Subscription = new Subscription();

  protected selectedFiles: File[] = [];
  protected previewImages: PreviewImage[] = [];
  protected adCategories: AdCategoryResponse[] = [];
  protected adSubcategories: AdSubcategoryResponse[] = [];
  protected adSpecifications: { [key: number]: number } = {};
  protected filteredAdSubcategories: AdSubcategoryResponse [] = [];
  protected allAdSubcategorySpecifications: AdSubcategorySpecificationResponse[] = [];
  protected adSpecificationOptions: { [key: number]: AdSpecificationOptionResponse[] } = {};
  protected adSubcategoryAssociatedSpecifications: AdSubcategoryAssociatedSpecificationResponse[] = [];

  public ngOnInit(): void {
    this.getAllAdCategories();
    this.getAllAdSubcategories();
    this.getAllAdSubcategorySpecifications();

    this.createCategoryIdSubscription();
    this.createSubcategoryIdSubscription();

    if (this.mode !== 'create') {
      this.patchFormWithData();
    }
  }

  public ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  protected dropImage(event: CdkDragDrop<string[]>): void {
    moveItemInArray(this.previewImages, event.previousIndex, event.currentIndex);
    this.previewImagesChange.emit(this.previewImages);
  }

  protected onRemoveImage(index: number): void {
    this.previewImages.splice(index, 1);
    this.imagesCount?.markAsTouched();
    this.imagesCount?.setValue(this.previewImages.length);
    this.previewImagesChange.emit(this.previewImages);
  }

  protected async onSelectImages(event: Event): Promise<void> {
    if (this.previewImages.length === 10) {
      return;
    }
    const input = event.target as HTMLInputElement;
    if (input.files) {
      const files: File[] = Array.from(input.files);
      this.selectedFiles = this.selectedFiles.concat(files);
      const previews: string[] = await this.imagProcessingService.generatePreviewUrls(files);
      previews.forEach((preview, index) => {
        if (this.previewImages.length === 10) {
          return;
        }
        this.previewImages.push({
          imageUrl: preview,
          file: files[index]
        });
      });
      this.selectedFiles = [];
      this.imagesCount?.markAsTouched();
      this.imagesCount?.setValue(this.previewImages.length);
      this.previewImagesChange.emit(this.previewImages);
    }
    input.value = '';
  }

  protected showIdentifierInfo(): void {
    this.alertService.showIdentifierInfo();
  }

  protected onChangeAdSpecifications(): void {
    this.changeAdSpecifications.emit(this.adSpecifications);
  }

  protected getSpecificationPlaceholder(adSpecificationName: string): string {
    return `Изберете ${adSpecificationName.toLowerCase()}...`;
  }

  private patchFormWithData(): void {
    if (!this.adDetails) {
      return;
    }
    this.adSubcategoryId?.enable();
    this.title?.setValue(this.adDetails.title);
    this.identifier?.setValue(this.adDetails.identifier);
    this.description?.setValue(this.adDetails.description);
    this.adCategoryId?.setValue(this.adDetails.adCategoryId);
    this.adSubcategoryId?.setValue(this.adDetails.adSubcategoryId);

    this.adSpecifications = this.adDetails.adSelectedSpecificationOptions;
    this.onChangeAdSpecifications();

    if (this.mode === 'update') {
      this.previewImages = this.adDetails.imageIds.map(x => {
        return {
          imageId: x,
          imageUrl: `${environment.apiImageAdUrl}/additional/${this.adId}/${x}`,
          file: undefined
        };
      });
      this.previewImagesChange.emit(this.previewImages);
      this.imagesCount?.setValue(this.previewImages.length);
    }
  }

  private getAllAdCategories(): void {
    const subscription: Subscription = this.adCategoryStateService.getAllAdCategories().subscribe({
      next: adCategories => {
        if (!!adCategories) {
          this.adCategories = adCategories;
        } else {
          this.adCategoryApiService.getAllAdCategories().subscribe({
            next: response => {
              this.adCategoryStateService.setAllAdCategories(response);
            }
          });
        }
      }
    });
    this.subs.add(subscription);
  }

  private getAllAdSubcategories(): void {
    const subscription: Subscription = this.adSubcategoryStateService.getAllAdSubcategories().subscribe({
      next: adSubcategories => {
        if (!!adSubcategories) {
          this.adSubcategories = adSubcategories;
          this.filterAdSubcategories();
        } else {
          this.adSubcategoryApiService.getAllAdSubcategories().subscribe({
            next: response => {
              this.adSubcategoryStateService.setAllAdSubcategories(response);
            }
          });
        }
      }
    });
    this.subs.add(subscription);
  }

  private getAllAdSubcategorySpecifications(): void {
    const subscription: Subscription = this.adSubcategorySpecificationStateService.getAllAdSubcategorySpecifications().subscribe({
      next: allAdSubcategorySpecifications => {
        if (!!allAdSubcategorySpecifications) {
          this.allAdSubcategorySpecifications = allAdSubcategorySpecifications;
          this.mapAdSubcategorySpecifications();
        } else {
          this.adSubcategorySpecificationApiService.getAllAdSubcategorySpecifications().subscribe({
            next: response => {
              this.adSubcategorySpecificationStateService.setAllAdSubcategorySpecifications(response);
            }
          });
        }
      }
    });
    this.subs.add(subscription);
  }

  private getAdSpecificationOptions(adSpecificationId: number): void {
    const subscription: Subscription = this.adSpecificationOptionStateService.getAdSpecificationOptions(adSpecificationId).subscribe({
      next: adSpecificationOptions => {
        if (!!adSpecificationOptions) {
          this.adSpecificationOptions[adSpecificationId] = adSpecificationOptions;
        } else {
          this.adSpecificationOptionApiService.getAdSpecificationOptions(adSpecificationId).subscribe({
            next: response => {
              this.adSpecificationOptions[adSpecificationId] = response;
              this.adSpecificationOptionStateService.setAdSpecificationOptions(adSpecificationId, this.adSpecificationOptions[adSpecificationId]);
            }
          });
        }
      }
    });
    this.subs.add(subscription);
  }

  private filterAdSubcategories(): void {
    if (!!this.adCategoryId?.value && !!this.adSubcategories) {
      this.filteredAdSubcategories = this.adSubcategories.filter(x => x.adCategoryId === this.adCategoryId?.value);
    }
  }

  private mapAdSubcategorySpecifications(): void {
    if (!!this.adSubcategoryId?.value && this.allAdSubcategorySpecifications.length !== 0) {
      const index: number = this.allAdSubcategorySpecifications.findIndex(x => x.adSubcategoryId === this.adSubcategoryId?.value);
      if (index >= 0) {
        this.adSubcategoryAssociatedSpecifications = this.allAdSubcategorySpecifications[index].associatedSpecifications;
        for (const adSubcategoryAssociatedSpecification of this.adSubcategoryAssociatedSpecifications) {
          this.getAdSpecificationOptions(adSubcategoryAssociatedSpecification.adSpecificationId);
        }
      }
    }
  }

  private createCategoryIdSubscription(): void {
    const valueChangesSubscription = this.adCategoryId?.valueChanges.subscribe({
      next: adCategoryId => {
        this.adSubcategoryId?.setValue(null);
        if (!!adCategoryId) {
          this.adSubcategoryId?.enable();
          this.adSubcategoryId?.setValue(null);
          this.filterAdSubcategories();
        } else {
          this.adSubcategoryId?.disable();
        }
      }
    });
    this.subs.add(valueChangesSubscription);
  }

  private createSubcategoryIdSubscription(): void {
    const valueChangesSubscription = this.adSubcategoryId?.valueChanges.subscribe({
      next: adSubcategoryId => {
        this.adSpecifications = {};
        this.onChangeAdSpecifications();
        this.adSubcategoryAssociatedSpecifications = [];
        if (!!adSubcategoryId) {
          this.mapAdSubcategorySpecifications();
        } else {
          this.adSubcategoryAssociatedSpecifications = [];
        }
      }
    });
    this.subs.add(valueChangesSubscription);
  }

  get title() {
    return this.form.get('title');
  }

  get identifier() {
    return this.form.get('identifier');
  }

  get description() {
    return this.form.get('description');
  }

  get adCategoryId() {
    return this.form.get('adCategoryId');
  }

  get adSubcategoryId() {
    return this.form.get('adSubcategoryId');
  }

  get imagesCount() {
    return this.form.get('imagesCount');
  }
}
